import { useState, useEffect } from 'react';
import { SafeAreaInsets, DeviceInfo, safeAreaDetector } from '../utils/safeAreaDetector';

/**
 * 安全区域Hook
 * 提供安全区域相关的状态和工具函数
 */
export const useSafeArea = () => {
    const [safeAreaInsets, setSafeAreaInsets] = useState<SafeAreaInsets>(
        safeAreaDetector.getSafeAreaInsets()
    );
    const [deviceInfo] = useState<DeviceInfo>(
        safeAreaDetector.getDeviceInfo()
    );

    useEffect(() => {
        const handleInsetsChange = (insets: SafeAreaInsets) => {
            setSafeAreaInsets(insets);
        };

        safeAreaDetector.addListener(handleInsetsChange);

        return () => {
            safeAreaDetector.removeListener(handleInsetsChange);
        };
    }, []);

    /**
     * 生成安全区域样式
     */
    const generateSafeAreaStyle = (options: {
        top?: boolean;
        bottom?: boolean;
        left?: boolean;
        right?: boolean;
        extraTop?: number;
    } = {}) => {
        return safeAreaDetector.generateSafeAreaCSS(options);
    };

    /**
     * 获取推荐的顶部间距
     */
    const getRecommendedTopPadding = () => {
        return safeAreaDetector.getRecommendedTopPadding();
    };

    /**
     * 检查是否需要安全区域适配
     */
    const needsSafeAreaAdaptation = () => {
        return deviceInfo.hasNotch || deviceInfo.isDingTalk || safeAreaInsets.top > 0;
    };

    /**
     * 获取安全区域CSS类名
     */
    const getSafeAreaClasses = (options: {
        top?: boolean;
        bottom?: boolean;
        left?: boolean;
        right?: boolean;
        dingTalkOptimized?: boolean;
    } = {}) => {
        const { top = true, bottom = false, left = false, right = false, dingTalkOptimized = true } = options;
        const classes: string[] = [];

        if (top) {
            classes.push(dingTalkOptimized && deviceInfo.isDingTalk ? 'dingtalk-safe-top' : 'safe-area-top');
        }
        if (bottom) classes.push('safe-area-bottom');
        if (left) classes.push('safe-area-left');
        if (right) classes.push('safe-area-right');

        return classes.join(' ');
    };

    return {
        safeAreaInsets,
        deviceInfo,
        generateSafeAreaStyle,
        getRecommendedTopPadding,
        needsSafeAreaAdaptation,
        getSafeAreaClasses,
        // 便捷属性
        isIOS: deviceInfo.isIOS,
        isAndroid: deviceInfo.isAndroid,
        isDingTalk: deviceInfo.isDingTalk,
        hasNotch: deviceInfo.hasNotch,
        topInset: safeAreaInsets.top,
        bottomInset: safeAreaInsets.bottom,
        leftInset: safeAreaInsets.left,
        rightInset: safeAreaInsets.right,
    };
};
